/**
 * Attendance Records: who should have worked, and what happened.
 *
 * Deliberately not the Team page. That one lists the days the system has
 * records *for*; this one starts from the roster, so the rows it exists to
 * show are the ones with no record at all — a no-show never produces an
 * AttendanceDay row, because the rollup is only written for somebody who
 * opened a session.
 *
 * Everyone with an employee record appears together, with no role split: a
 * recruiter, a manager and a company admin are all people who were or were not
 * at work. The server decides every status; nothing here infers one, so the
 * page and the report can never disagree about what "absent" means.
 */
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { client } from '../api/client';
import AttendanceMonthlyTable from '../components/AttendanceMonthlyTable';
import AttendanceDetailPanel from '../components/AttendanceDetailPanel';
import EmployeeDetailPanel from '../components/EmployeeDetailPanel';
import { titleCaseName, titleCaseNameOr } from '../lib/titleCase';
import type {
  AttendanceMonthly,
  AttendanceRecord,
  AttendanceRecordStatus,
  Page,
} from '../types';

const PAGE_SIZE = 100;

/**
 * How each status reads and reads *as*.
 *
 * Three tones, and the split is the point. Amber is "somebody should look":
 * a late start, a shift that should have begun, an absence. Grey is "the day
 * was never theirs" — leave, a holiday, a weekly off, a shift not yet due —
 * and none of those is a finding about the person. Unmonitored is grey for
 * exactly that reason: it is the system admitting it has nothing to say, not
 * an accusation.
 */
const STATUS: Record<AttendanceRecordStatus, { label: string; tone: string }> = {
  present: { label: 'Present', tone: 'bg-emerald-100 text-emerald-700' },
  late: { label: 'Late', tone: 'bg-amber-100 text-amber-800' },
  half_day: { label: 'Half day', tone: 'bg-amber-100 text-amber-700' },
  unmonitored: { label: 'Unmonitored', tone: 'bg-gray-100 text-gray-600' },
  absent: { label: 'Absent', tone: 'bg-red-100 text-red-700' },
  not_yet_started: { label: 'Not yet started', tone: 'bg-amber-100 text-amber-800' },
  not_due_yet: { label: 'Not due yet', tone: 'bg-gray-100 text-gray-500' },
  leave: { label: 'On leave', tone: 'bg-sky-100 text-sky-700' },
  holiday: { label: 'Holiday', tone: 'bg-sky-100 text-sky-700' },
  weekly_off: { label: 'Weekly off', tone: 'bg-gray-100 text-gray-500' },
  no_shift_assigned: { label: 'No shift assigned', tone: 'bg-gray-100 text-gray-600' },
};

/** The order the filter offers them: what needs attention first. */
const FILTERABLE: AttendanceRecordStatus[] = [
  'present',
  'late',
  'absent',
  'not_yet_started',
  'not_due_yet',
  'half_day',
  'unmonitored',
  'leave',
  'holiday',
  'weekly_off',
  'no_shift_assigned',
];

function duration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

function Metric({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'warn' | 'bad';
}) {
  const colour =
    tone === 'bad'
      ? 'text-red-700'
      : tone === 'warn'
        ? 'text-amber-700'
        : 'text-gray-900';
  return (
    <div className="rounded-xl bg-white p-4 ring-1 ring-gray-200">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${colour}`}>{value}</p>
    </div>
  );
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export default function AttendanceRecordsPage() {
  /**
   * Day or month. Two readings of the same roster through the same ladder —
   * the monthly figures are the daily ones tallied, not a second query that
   * could disagree with them.
   */
  const [view, setView] = useState<'day' | 'month'>('day');
  const [workDate, setWorkDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [status, setStatus] = useState<AttendanceRecordStatus | ''>('');
  const [search, setSearch] = useState('');
  /** Whose profile the slide-out is showing, by user id. */
  const [viewing, setViewing] = useState<string | null>(null);
  /** Which scored day is open, by attendance row id. */
  const [openingDay, setOpeningDay] = useState<AttendanceRecord | null>(null);

  const { data, isLoading } = useQuery<Page<AttendanceRecord>>({
    // No companyId here on purpose: the acting-company interceptor adds it, so
    // the owner's existing header picker drives this page with no second
    // control on it.
    queryKey: ['attendance-records', workDate, status, search],
    queryFn: async () =>
      (
        await client.get('/api/v1/attendance/records', {
          params: {
            workDate: workDate || undefined,
            status: status || undefined,
            q: search.trim() || undefined,
            limit: PAGE_SIZE,
          },
        })
      ).data,
  });

  const rows = data?.items ?? [];

  /**
   * Counted from the unfiltered roster, so the cards do not silently describe
   * whatever the filter happens to be showing.
   */
  const { data: all } = useQuery<Page<AttendanceRecord>>({
    queryKey: ['attendance-records', 'summary', workDate],
    queryFn: async () =>
      (
        await client.get('/api/v1/attendance/records', {
          params: { workDate: workDate || undefined, limit: 200 },
        })
      ).data,
  });

  const monthly = useQuery<Page<AttendanceMonthly>>({
    // Same deal as above: no companyId, the interceptor supplies it.
    queryKey: ['attendance-monthly', year, month, search],
    // Skipped entirely in day view rather than fetched and discarded — a month
    // walks every day for every person, so it is not free.
    enabled: view === 'month',
    queryFn: async () =>
      (
        await client.get('/api/v1/attendance/records/monthly', {
          params: { year, month, q: search.trim() || undefined, limit: 200 },
        })
      ).data,
  });

  const everyone = all?.items ?? [];
  const count = (s: AttendanceRecordStatus) =>
    everyone.filter((r) => r.status === s).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Attendance Records</h1>
          <p className="text-sm text-gray-500">
            Everyone on the roster for one day, including the people who never
            clocked in.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-gray-500">
            View
            <select
              className="input mt-1"
              value={view}
              onChange={(e) => setView(e.target.value as 'day' | 'month')}
            >
              <option value="day">One day</option>
              <option value="month">Whole month</option>
            </select>
          </label>

          {view === 'day' ? (
            <label className="text-xs text-gray-500">
              Date
              <input
                type="date"
                className="input mt-1"
                value={workDate}
                onChange={(e) => setWorkDate(e.target.value)}
              />
            </label>
          ) : (
            <>
              <label className="text-xs text-gray-500">
                Month
                <select
                  className="input mt-1"
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                >
                  {MONTHS.map((name, i) => (
                    <option key={name} value={i + 1}>{name}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-gray-500">
                Year
                <select
                  className="input mt-1"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                >
                  {/* This year and the four before it. Attendance older than
                      that is a question for an export, not a picker. */}
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(
                    (y) => (
                      <option key={y} value={y}>{y}</option>
                    ),
                  )}
                </select>
              </label>
            </>
          )}
          {view === 'day' && (
          <label className="text-xs text-gray-500">
            Status
            <select
              className="input mt-1"
              value={status}
              onChange={(e) =>
                setStatus(e.target.value as AttendanceRecordStatus | '')
              }
            >
              <option value="">All</option>
              {FILTERABLE.map((s) => (
                <option key={s} value={s}>
                  {STATUS[s].label}
                </option>
              ))}
            </select>
          </label>
          )}
          <label className="text-xs text-gray-500">
            Search
            <input
              spellCheck={false}
              className="input mt-1"
              placeholder="Name or code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
        </div>
      </div>

      {/* Day counts. A month's totals are in its own table, per person, and
          summing them into five cards would invite reading a month as a day. */}
      {view === 'day' && (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
        <Metric label="Present" value={String(count('present'))} />
        <Metric
          label="Late"
          value={String(count('late'))}
          tone={count('late') > 0 ? 'warn' : 'neutral'}
        />
        <Metric
          label="Absent"
          value={String(count('absent'))}
          tone={count('absent') > 0 ? 'bad' : 'neutral'}
        />
        <Metric
          label="Not yet started"
          value={String(count('not_yet_started'))}
          tone={count('not_yet_started') > 0 ? 'warn' : 'neutral'}
        />
        <Metric label="On leave" value={String(count('leave'))} />
      </div>

      )}

      {view === 'month' ? (
        <AttendanceMonthlyTable
          rows={monthly.data?.items ?? []}
          isLoading={monthly.isLoading}
        />
      ) : isLoading ? (
        <div className="flex justify-center py-12 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">Person</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium">Late By</th>
                {/* Attendance and responsiveness are different questions and
                    now read as two columns instead of one number wearing both
                    labels. Worked comes first because it is the one the status
                    is answerable from: somebody on the clock nine hours is
                    present, whatever their check-ins did. */}
                <th className="px-4 py-2.5 font-medium">Worked</th>
                <th className="px-4 py-2.5 font-medium">
                  Responsiveness
                  <span className="ml-1 font-normal normal-case text-gray-400">
                    (check-ins)
                  </span>
                </th>
                <th className="px-4 py-2.5 font-medium">Shift</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                /* The whole row opens the day.
                 *
                 * It used to be a "View day" link that appeared only where a
                 * rollup existed — which hid the days most worth opening, since
                 * a day nothing scored is exactly the one somebody is asking
                 * about. The drill-down is keyed by person and date now, so
                 * every row has something to show, even if that something is
                 * "no check-ins were recorded". */
                <tr
                  key={row.userId}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => setOpeningDay(row)}
                  title="Open This Day's Check-ins"
                >
                  <td className="px-4 py-2.5 text-gray-900">
                    <button
                      type="button"
                      className="text-left font-medium hover:text-indigo-700 hover:underline"
                      // Stopped, or opening the profile would also open the
                      // day behind it.
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewing(row.userId);
                      }}
                      title="Open Profile"
                    >
                      {titleCaseName(row.userName) ?? row.employeeCode}
                    </button>
                    <span className="block text-xs text-gray-500">
                      {row.designation ?? row.employeeCode}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <span className={`badge ${STATUS[row.status].tone}`}>
                      {STATUS[row.status].label}
                    </span>
                    {/* Said plainly rather than left for somebody to work out
                        from a blank availability column. */}
                    {row.inProgress && (
                      <span className="ml-1.5 text-xs text-gray-500">still on shift</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-700">
                    {row.lateByMinutes != null && row.lateByMinutes > 0 ? (
                      <span className="text-amber-700">
                        {duration(row.lateByMinutes)}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  {/* Time on the clock, live while the shift is running.
                      This is what the status is read off now, so it belongs
                      beside it rather than three columns away. */}
                  <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-700">
                    {row.workedMinutes != null && row.workedMinutes > 0 ? (
                      <>
                        {duration(row.workedMinutes)}
                        {row.inProgress && (
                          <span className="ml-1 text-xs text-gray-400">so far</span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  {/* Responsiveness, not attendance. Blank rather than zero: a
                      day nobody scored has no percentage, and printing 0%
                      would read as a finding about the person instead of an
                      absence of data. */}
                  <td className="px-4 py-2.5 text-gray-700">
                    {row.availabilityPct != null ? (
                      <>
                        <span className="tabular-nums">
                          {`${Number(row.availabilityPct).toFixed(0)}%`}
                        </span>
                        <span className="ml-1.5 text-xs text-gray-500">
                          {row.respondedPings}/{row.expectedPings}
                        </span>
                      </>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">
                    {row.shiftPolicyName ?? (
                      <span className="text-amber-700">none assigned</span>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                    Nobody matches that.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {view === 'day' && data && data.total > rows.length && (
        <p className="text-xs text-gray-500">
          Showing {rows.length} of {data.total}. Narrow the search to see the rest.
        </p>
      )}

      {viewing && (
        <EmployeeDetailPanel
          key={viewing}
          userId={viewing}
          onClose={() => setViewing(null)}
        />
      )}

      {openingDay && (
        <AttendanceDetailPanel
          key={`${openingDay.userId}-${workDate}`}
          userId={openingDay.userId}
          workDate={workDate}
          name={titleCaseNameOr(openingDay.userName, 'this employee')}
          onClose={() => setOpeningDay(null)}
        />
      )}
    </div>
  );
}
